import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Percent, Save, RotateCcw, DollarSign, Tag, Info, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_TIERS = [
    { label: 'Menores a $99 MXN', min_price: 0, max_price: 99, fixed_cost: 25 },
    { label: 'De $99 a $149 MXN', min_price: 99, max_price: 149, fixed_cost: 30 },
    { label: 'De $149 a $299 MXN', min_price: 149, max_price: 299, fixed_cost: 37 },
    { label: 'Desde $299 MXN', min_price: 299, max_price: null, fixed_cost: 0 },
];

export default function AdminComisiones() {
    const [cats, setCats] = useState([]);
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    const load = async () => {
        const [{ data: categories }, { data: config }] = await Promise.all([
            api.get('/categories'),
            api.get('/admin/commissions').catch(() => ({ data: null })),
        ]);
        setCats(categories || []);
        if (config) {
            // Merge: ensure every category appears in config.categories
            const configMap = Object.fromEntries((config.categories || []).map(c => [c.slug, c.commission_pct]));
            setCfg({
                ...config,
                categories: categories.map(c => ({
                    slug: c.slug,
                    name: c.name,
                    commission_pct: configMap[c.slug] ?? 0,
                })),
                fixed_cost_tiers: config.fixed_cost_tiers?.length ? config.fixed_cost_tiers : DEFAULT_TIERS,
            });
        } else {
            setCfg({
                default_commission_pct: 10,
                categories: categories.map(c => ({ slug: c.slug, name: c.name, commission_pct: 0 })),
                fixed_cost_tiers: DEFAULT_TIERS,
            });
        }
    };

    useEffect(() => { load(); }, []);

    const setCatCommission = (slug, val) => {
        setCfg(prev => ({
            ...prev,
            categories: prev.categories.map(c => c.slug === slug ? { ...c, commission_pct: val } : c),
        }));
    };

    const setTier = (i, field, val) => {
        setCfg(prev => ({
            ...prev,
            fixed_cost_tiers: prev.fixed_cost_tiers.map((t, j) => j === i ? { ...t, [field]: val } : t),
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/commissions', cfg);
            toast.success('Configuración de comisiones guardada');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
        finally { setSaving(false); }
    };

    if (!cfg) return <div className="text-sm text-muted-foreground">Cargando…</div>;

    const filtered = cfg.categories.filter(c =>
        !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search.toLowerCase())
    );

    return (
        <div className="space-y-6 max-w-4xl">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Comisiones</h1>
                    <p className="text-sm text-muted-foreground">Configura las comisiones por categoría y los costos fijos de la plataforma.</p>
                </div>
                <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                    <Save className="h-4 w-4 mr-2" />{saving ? 'Guardando…' : 'Guardar cambios'}
                </Button>
            </div>

            {/* Listing types info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-card p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-emerald-500 text-emerald-700">Gratuita</Badge>
                        <span className="text-sm font-medium">Publicación Gratuita</span>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-1">
                        <li>✓ Duración: 90 días (configurable por plan)</li>
                        <li>✓ Comisión por venta: <strong>0%</strong></li>
                        <li>✓ Sin costo de listado</li>
                        <li>• Disponible en todos los planes</li>
                    </ul>
                </div>
                <div className="rounded-xl border bg-card p-4 border-amber-300/50 bg-amber-50/50">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-amber-500 text-white">Prime</Badge>
                        <span className="text-sm font-medium">Publicación Prime</span>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-1">
                        <li>✓ Duración: <strong>Ilimitada</strong></li>
                        <li>✓ Comisión por venta: basada en categoría (esta tabla)</li>
                        <li>✓ Mayor visibilidad y prioridad</li>
                        <li>• Requiere plan con Prime habilitado</li>
                    </ul>
                </div>
            </div>

            {/* Default commission */}
            <div className="rounded-xl border bg-card p-5 space-y-3">
                <div className="flex items-center gap-2"><Percent className="h-4 w-4 text-red-600" /><h2 className="font-medium">Comisión por defecto</h2></div>
                <p className="text-xs text-muted-foreground">Se aplica a categorías sin comisión configurada en publicaciones Prime.</p>
                <div className="flex items-center gap-3">
                    <div className="w-40">
                        <Label className="text-xs">Comisión base (%)</Label>
                        <Input type="number" min="0" max="100" step="0.5"
                            value={cfg.default_commission_pct}
                            onChange={e => setCfg(prev => ({ ...prev, default_commission_pct: parseFloat(e.target.value) || 0 }))}
                            className="mt-1" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">Aplica cuando la categoría del anuncio no está en la tabla de abajo o tiene 0%.</p>
                </div>
            </div>

            {/* Category commission table */}
            <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-red-600" /><h2 className="font-medium">Comisiones por categoría</h2></div>
                    <Input placeholder="Buscar categoría…" value={search} onChange={e => setSearch(e.target.value)} className="w-52 h-8 text-sm" />
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-muted-foreground">
                                <th className="text-left px-5 py-2.5 w-8">#</th>
                                <th className="text-left px-4 py-2.5">Categoría</th>
                                <th className="text-left px-4 py-2.5 w-32">Slug</th>
                                <th className="text-left px-4 py-2.5 w-36">Comisión (%)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c, i) => (
                                <tr key={c.slug} className="border-b last:border-0 hover:bg-neutral-50/80">
                                    <td className="px-5 py-2 text-muted-foreground text-xs">{i + 1}</td>
                                    <td className="px-4 py-2 font-medium">{c.name}</td>
                                    <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{c.slug}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-1.5">
                                            <Input type="number" min="0" max="100" step="0.5"
                                                value={c.commission_pct}
                                                onChange={e => setCatCommission(c.slug, parseFloat(e.target.value) || 0)}
                                                className="w-20 h-8 text-sm" />
                                            <span className="text-muted-foreground text-xs">%</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Fixed cost tiers */}
            <div className="rounded-xl border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-red-600" /><h2 className="font-medium">Costos fijos adicionales</h2></div>
                <p className="text-xs text-muted-foreground">Cargo adicional por unidad vendida según el precio del artículo. Se aplica además de la comisión porcentual.</p>
                <div className="space-y-3">
                    {cfg.fixed_cost_tiers.map((tier, i) => (
                        <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-3 rounded-lg border">
                            <div>
                                <Label className="text-xs">Descripción</Label>
                                <Input value={tier.label} onChange={e => setTier(i, 'label', e.target.value)} className="mt-1 h-8 text-sm" />
                            </div>
                            <div>
                                <Label className="text-xs">Precio mínimo ($)</Label>
                                <Input type="number" min="0" value={tier.min_price} onChange={e => setTier(i, 'min_price', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" />
                            </div>
                            <div>
                                <Label className="text-xs">Precio máximo ($)</Label>
                                <Input type="number" min="0" placeholder="Sin límite"
                                    value={tier.max_price ?? ''}
                                    onChange={e => setTier(i, 'max_price', e.target.value ? parseFloat(e.target.value) : null)}
                                    className="mt-1 h-8 text-sm" />
                            </div>
                            <div>
                                <Label className="text-xs">Costo fijo ($)</Label>
                                <Input type="number" min="0" step="0.5" value={tier.fixed_cost} onChange={e => setTier(i, 'fixed_cost', parseFloat(e.target.value) || 0)} className="mt-1 h-8 text-sm" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Stripe sync info */}
            <div className="rounded-xl border bg-blue-50/60 border-blue-200 p-4 flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                    <div className="text-sm font-medium text-blue-900">Sincronización con Stripe</div>
                    <p className="text-xs text-blue-700 mt-1">
                        Las comisiones configuradas se aplican automáticamente al crear nuevos checkout sessions en Stripe.
                        Para anuncios Prime, se calcula la comisión de la categoría correspondiente.
                        Para anuncios Gratuita, la comisión es 0% (el comprador paga solo el precio del producto).
                        Los valores se guardan en los metadatos de cada transacción de Stripe para auditoría.
                    </p>
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700">
                    <Save className="h-4 w-4 mr-2" />{saving ? 'Guardando…' : 'Guardar configuración'}
                </Button>
            </div>
        </div>
    );
}
